import React, { useState, useEffect } from 'react';
import { FaBrain, FaInfoCircle } from 'react-icons/fa';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

function CircleScore({ percent = 0, animated = true }) {
  const [animatedPercent, setAnimatedPercent] = useState(0);
  const [animatedColor, setAnimatedColor] = useState('#dc2626');
  
  const clamped = Math.max(0, Math.min(100, percent));
  const circumference = 2 * Math.PI * 45;
  const offset = circumference * (1 - animatedPercent / 100);
  
  // Calculate color based on score (0-100)
  const getScoreColor = (score) => {
    if (score <= 20) return '#dc2626'; // red
    if (score <= 40) return '#ea580c'; // orange
    if (score <= 60) return '#d97706'; // yellow-orange
    if (score <= 80) return '#65a30d'; // yellow-green
    return '#16a34a'; // green
  };
  
  const finalColor = getScoreColor(clamped);
  
  useEffect(() => {
    if (!animated) {
      setAnimatedPercent(clamped);
      setAnimatedColor(finalColor);
      return;
    }
    
    // Animate the percentage
    const duration = 1500; // 1.5 seconds
    const steps = 60; // 60 steps for smooth animation
    const stepDuration = duration / steps;
    const stepSize = clamped / steps;
    
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const newPercent = Math.min(stepSize * currentStep, clamped);
      setAnimatedPercent(newPercent);
      
      // Update color as it animates
      setAnimatedColor(getScoreColor(newPercent));
      
      if (currentStep >= steps) {
        clearInterval(interval);
        setAnimatedPercent(clamped);
        setAnimatedColor(finalColor);
      }
    }, stepDuration);
    
    return () => clearInterval(interval);
  }, [clamped, animated, finalColor]);
  
  return (
    <svg width="130" height="130" viewBox="0 0 130 130">
      <circle cx="65" cy="65" r="45" fill="none" stroke="#eee" strokeWidth="10" />
      <circle
        cx="65"
        cy="65"
        r="45"
        fill="none"
        stroke={animatedColor}
        strokeWidth="10"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 65 65)"
        style={{
          transition: animated ? 'stroke 0.1s ease-out' : 'none'
        }}
      />
      <text x="65" y="72" textAnchor="middle" fontSize="22" fontWeight="700" fill="#333">
        {Math.round(animatedPercent)}%
      </text>
    </svg>
  );
}

export default function HudsonAIHealthScore({ detail }) {
  if (!detail) return null;
  
  return (
    <div className="bg-white rounded-lg p-3" style={{ borderRadius: 12, width: '280px', minWidth: '280px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: '240px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 8, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ 
            width: '20px', 
            height: '20px', 
            backgroundColor: '#dbeafe', 
            borderRadius: '4px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: '12px',
            color: '#3b82f6'
          }}>
            <FaBrain />
          </div>
          Hudson AI Score
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip 
                id="ai-score-tooltip" 
                className="custom-tooltip"
                style={{ 
                  backgroundColor: '#6b7280', 
                  color: 'white',
                  opacity: 1,
                  '--bs-tooltip-bg': '#6b7280',
                  '--bs-tooltip-color': 'white',
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word'
                }}
              >
                This is a score of the overall equipment health based on age, history, etc
              </Tooltip>
            }
          >
            <div style={{ 
              width: '16px', 
              height: '16px', 
              backgroundColor: '#e5e7eb', 
              borderRadius: '50%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '10px',
              color: '#9ca3af',
              cursor: 'help'
            }}>
              <FaInfoCircle />
            </div>
          </OverlayTrigger>
        </div>
        <CircleScore 
          key={detail.id} 
          percent={detail.aiScorePercent || 0} 
          animated={true} 
        />
        <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', marginTop: 4 }}>*overall health score</div>
      </div>
    </div>
  );
}
